import React from 'react';
import { Link } from 'react-router-dom';
import { MailIcon, MapPinIcon, CalendarIcon, CheckIcon, LinkedinIcon } from 'lucide-react';
import { Container, Section, Kicker } from '../components/ui/Primitives';
import { Photo } from '../components/ui/Photo';
import { PHOTOS } from '../data/photos';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { MeetingScheduler } from '../components/MeetingScheduler';
import { SERVICE_LINES } from '../data/services';
import { submitLead } from '../lib/leads';
import { track } from '../lib/analytics';
import { LINKEDIN_URL, CONTACT_EMAIL } from '../lib/seo';
const ENQUIRY_TYPES = [{
  id: 'intelligence',
  label: 'A market or sector question'
}, {
  id: 'growth',
  label: 'Growth, pricing or pipeline'
}, {
  id: 'ai',
  label: 'AI adoption or readiness'
}, {
  id: 'other',
  label: 'Something else'
}];
export function Contact() {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [enquiry, setEnquiry] = React.useState('intelligence');
  const [message, setMessage] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const routedLine = SERVICE_LINES.find(line => line.id === enquiry);
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName: name,
      organisation,
      consent,
      source: 'contact-form',
      line: enquiry === 'other' ? undefined : enquiry,
      notes: message
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    setSent(true);
  };
  return <>
      <section className="surface-dark" data-id="element-2485" data-mp="1m5ipzxbknjar">
        <Container className="grid gap-12 py-14 sm:py-20 lg:grid-cols-[1.2fr_0.8fr] lg:items-center" data-id="element-2486" data-mp="1h8iu2f8frwxd">
          <div data-id="element-2487" data-mp="1w59oea396ig4">
            <Kicker data-id="element-2488" data-mp="1w5t55nh630sk">Contact</Kicker>
            <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-2489" data-mp="1w4rs5dglc487">
              Tell us the decision in front of you
            </h1>
            <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-2490" data-mp="13hnj8xedn5r5">
              A discovery call lasts thirty minutes. We will tell you honestly whether we are the right firm for the
              problem, and what we would do first if we are.
            </p>
          </div>
          <Photo photo={PHOTOS.teamSession} aspect="aspect-[4/3]" tone="dark" eager data-id="element-2491" data-mp="18fp15f02gz11" />
        </Container>
      </section>

      <Section data-id="element-2492" data-mp="kp34ejvij16c">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-2493" data-mp="kojm321yaars">
          <div data-id="element-2494" data-mp="fs2xk6qu9ue6">
            <h2 className="font-display text-[26px] text-ink" data-id="element-2495" data-mp="fqi4ymo34liy">Send us a message</h2>
            {sent ? <div className="mt-6 rounded-md border border-line bg-mist p-7" data-id="element-2496" data-mp="2rd24wfj2onvo">
                <div className="flex items-center gap-2 text-gold" data-id="element-2497" data-mp="2w6ep8dgpy8im">
                  <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2498" data-mp="2hgqq9iw0fly2" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-2499" data-mp="2hedzqhc6s39e">Received</span>
                </div>
                <h3 className="mt-4 font-display text-[22px] text-ink" data-id="element-2500" data-mp="3a5e44tefj91l">Thank you, we have your enquiry</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-2501" data-mp="3f4r1i82xfyif">
                  {routedLine ? `We have routed this to the ${routedLine.name} team. ` : 'We have routed this to the right team. '}
                  You will hear from us within one working day. If it is urgent, book a call using the scheduler.
                </p>
              </div> : <form onSubmit={onSubmit} className="mt-6 space-y-5" data-id="element-2502" data-mp="1tre6jp323vhi">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-2503" data-mp="1tojmvzrvaep5">
                  <Field id="c-name" label="Your name" required data-id="element-2504" data-mp="29ysr7g8659ff">
                    <input id="c-name" type="text" required value={name} onChange={event => setName(event.target.value)} className={inputClass} data-id="element-2505" data-mp="256smlah5lsci" />
                  </Field>
                  <Field id="c-email" label="Work email" required error={error ?? undefined} data-id="element-2506" data-mp="2svtw711q39jd">
                    <input id="c-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-2507" data-mp="2nytuf2bwosy2" />
                  </Field>
                </div>
                <Field id="c-org" label="Organisation" required data-id="element-2508" data-mp="13h5o3c060dr5">
                  <input id="c-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-2509" data-mp="3f5k5uab4ns3q" />
                </Field>
                <Field id="c-enquiry" label="What is your enquiry about" required hint="We use this to route your message to the right service line." data-id="element-2510" data-mp="3sxqoq45o0d6v">
                  <select id="c-enquiry" value={enquiry} onChange={event => setEnquiry(event.target.value)} className={inputClass} data-id="element-2511" data-mp="6rlddind6ouz">
                    {ENQUIRY_TYPES.map(type => <option key={type.id} value={type.id} data-id="element-2512" data-mp="2ck9zppy1ybo5">
                        {type.label}
                      </option>)}
                  </select>
                </Field>
                <Field id="c-message" label="Your message" required data-id="element-2513" data-mp="3kspo7rox1exp">
                  <textarea id="c-message" required rows={5} value={message} onChange={event => setMessage(event.target.value)} placeholder="The decision you are facing, and any timing we should know about." className={inputClass} data-id="element-2514" data-mp="3fuebn978kqbs" />
                </Field>
                <ConsentCheckbox id="c-consent" checked={consent} onChange={setConsent} data-id="element-2515" data-mp="2mbb1uyh190d">
                  I am happy for Cavalla Solution to contact me about this enquiry. See our{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-2516" data-mp="cdp7tuz1fupt">
                    privacy notice
                  </Link>
                  .
                </ConsentCheckbox>
                <Button type="submit" size="lg" disabled={submitting} data-id="element-2517" data-mp="ledmmrw99z3r">
                  {submitting ? 'Sending' : 'Send enquiry'}
                </Button>
              </form>}
          </div>

          <div className="space-y-8" data-id="element-2518" data-mp="19ww44smzwxzb">
            <div className="rounded-md border border-line bg-mist p-6 sm:p-7" data-id="element-2519" data-mp="19vup6d2uzrcb">
              <div className="flex items-center gap-2 text-gold" data-id="element-2520" data-mp="v0olhwr8k4m5">
                <CalendarIcon className="h-4 w-4" aria-hidden="true" data-id="element-2521" data-mp="zyzuk59pib3b" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-2522" data-mp="m8wc7pdy71j5">Scheduler</span>
              </div>
              <h2 className="mt-4 font-display text-[22px] text-ink" data-id="element-2523" data-mp="qzl77eou6gn1">Book a discovery call</h2>
              <p className="mt-3 text-[15px] text-body" data-id="element-2524" data-mp="cb8663aw67lo">
                Thirty minutes with our founder. Choose a slot that suits your time zone.
              </p>
              <MeetingScheduler line={enquiry} className="mt-6" data-id="element-2525" data-mp="39qserkyii05" />
            </div>

            <div className="rounded-md border border-line p-6 sm:p-7" data-id="element-2526" data-mp="3bv3pul6qjag">
              <h2 className="font-display text-[20px] text-ink" data-id="element-2527" data-mp="7a2e87p22od7">Reach us directly</h2>
              <ul className="mt-5 space-y-4 text-[15px] text-body" data-id="element-2528" data-mp="nptn99w9uj93">
                <li className="flex gap-3" data-id="element-2529" data-mp="is1llo5trirp">
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2530" data-mp="isbblbqvdpxe" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline-offset-4 hover:underline" data-id="element-2531" data-mp="drdruuq7onts">
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li className="flex gap-3" data-id="element-2532" data-mp="4s8uhrqdz0j7">
                  <LinkedinIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2533" data-mp="4u3hrcp4r4qg" />
                  <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:underline" data-id="element-2534" data-mp="3w4lfgikktbqh">
                    Follow us on LinkedIn
                    <span className="sr-only" data-id="element-2535" data-mp="3h846fxzv0973">, opens in a new tab</span>
                  </a>
                </li>
                <li className="flex gap-3" data-id="element-2536" data-mp="3d8lwfx9ktij1">
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2537" data-mp="1sq1k687qp57f" />
                  <span data-id="element-2538" data-mp="1wr3au0nlc8xs">
                    Working across West and East Africa, with data held in the European Union.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>;
}